import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as NavigateArrow } from '../../assets/icons/device-management/navigate-back.svg';
import { ReactComponent as EditPencil } from '../../assets/icons/edit-pencil.svg';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { fetchAllPermissions, fetchAllRoles } from '../../redux/slices/RolesManagementSlice';
import { changeDateFormat } from '../../shared-functions/DateTime';
import { stringValidation } from '../../shared-functions/Validation';
import roles from '../../utils/api/roles';
import MyButton from '../button/Button';
import CheckBoxButtons from '../check-box-buttons/CheckBoxButtons';
import CustomInput from '../custom-input/CustomInput';
import ModalBox from '../modal-box/ModalBox';

interface RoleSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  onEdit?: (id: string) => void;
}

interface RoleDataProperties {
  _id: string;
  id: string;
  name: string;
  responsibility: string;
  permissions: string[];
  createdAt?: string;
  users: {
    name: string;
    status: string;
    email: string;
    phoneNumber: string;
  }[];
}

interface RoleFormState {
  name: string;
  responsibility: string;
  permissions: (string | number)[];
}

/**
 * Role side panel
 * @param properties
 * @returns
 */
function RoleSidePanel(properties: RoleSidePanelProperties) {
  const { t, i18n } = useTranslation('userRole');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const { permissionsList, page, limit } = useAppSelector((state) => state.roles);
  const [searchParameters] = useSearchParams();

  const [roleData, setRoleData] = useState<RoleDataProperties>();

  // Edit Mode State
  const [editMode, setEditMode] = useState(false);
  const [roleDetails, setRoleDetails] = useState<RoleFormState>({
    name: '',
    responsibility: '',
    permissions: [],
  });
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
    notification: false,
  });

  /**
   * Function to fetch role details
   * @param roleId
   */
  const handleFetchRoleDetails = async (roleId: string) => {
    roles
      .fetchRoleById(roleId)
      .then((response) => {
        setRoleData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('somethingWentWrong'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Toggle Edit Mode
   */
  const toggleEditMode = () => {
    if (editMode) {
      setEditMode(false);
      setShowFormErrors(false);
    } else if (roleData) {
      setRoleDetails({
        name: roleData.name,
        responsibility: roleData.responsibility,
        permissions: roleData.permissions,
      });
      setEditMode(true);
    }
  };

  /**
   * Handle Input Change
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setRoleDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };
  /**
   * Handle Edit Submit
   */
  const handleEditSubmit = () => {
    setShowFormErrors(true);
    if (!roleData) return;

    if (
      stringValidation(1, 100, roleDetails.name) &&
      stringValidation(1, 100, roleDetails.responsibility) &&
      roleDetails.permissions.length > 0
    ) {
      setFormLoading(true);
      const request = {
        name: roleDetails.name,
        responsibility: roleDetails.responsibility,
        permissions: roleDetails.permissions,
      };
      roles
        .editRoles(roleData._id, request)
        .then(() => {
          setFormLoading(false);
          setEditMode(false);
          dispatch(
            setNotificationMessage({
              message: t('roleUpdatedSuccessfully') || 'Role updated successfully',
              status: true,
              type: 'success',
              code: 0,
            }),
          );
          // Refresh details and list
          handleFetchRoleDetails(roleData._id);
          dispatch(fetchAllRoles({ page, limit }));
        })
        .catch((error) => {
          setFormLoading(false);
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * The delete the role confirm function
   */
  // const deleteConfirmFunction = (roleId: string) => {
  //   console.log('Deleting roleId:', roleId);
  //   roles
  //     .deleteRoles(roleId)
  //     .then(() => {
  //       dispatch(fetchAllRoles({ page, limit }));
  //       setModalView((previous) => ({ ...previous, status: false }));
  //       dispatch(
  //         setNotificationMessage({
  //           message: t('roleDeleted'),
  //           status: true,
  //           type: 'success',
  //           code: 0,
  //         }),
  //       );
  //     })
  //     .catch((error) => {
  //       setModalView((previous) => ({ ...previous, status: false }));
  //       dispatch(
  //         setNotificationMessage({
  //           message: error?.response?.data?.message,
  //           status: true,
  //           type: 'error',
  //           code: error?.response?.data?.code,
  //         }),
  //       );
  //     });
  // };

  /**
   * Handle Delete Click
   */
  // const handleDeleteClick = () => {
  //   if (!roleData) return;
  //   setModalView({
  //     status: true,
  //     title: '',
  //     header: false,
  //     content: (
  //       <DeleteUser
  //         title={t('areYouSureDeleteRole')}
  //         subTitle={t('deleteRoleWarning')}
  //         closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
  //         submitButton={
  //           <MyButton
  //             label={t('delete')}
  //             buttonType="error"
  //             onClickFunc={() => deleteConfirmFunction(roleData._id)}
  //             svgIcon={<DeleteIcon />}
  //           />
  //         }
  //       />
  //     ),
  //     rightCorner: '',
  //     notification: false,
  //   });
  // };

  useEffect(() => {
    let roleId;
    if (fetchIdFrom === 'queryParams') {
      roleId = searchParameters.get('roleId');
    } else if (fetchIdFrom === 'props') {
      roleId = id;
    }

    if (!roleId) {
      setRoleData(undefined);
      setEditMode(false);
      return;
    }

    handleFetchRoleDetails(roleId);
    setEditMode(false); // Reset edit mode on ID change
  }, [searchParameters, id]);

  useEffect(() => {
    if (permissionsList.length === 0) {
      dispatch(fetchAllPermissions());
    }
  }, []);

  return (
    <div className="user-details-side-panel" style={{ height: '100%' }}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />

      <div className="panel-header">
        {editMode ? (
          <div
            className="header-title-container"
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span
              role="presentation"
              onClick={toggleEditMode}
              style={{ cursor: 'pointer', display: 'flex' }}>
              <NavigateArrow
                style={{
                  transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </span>
            <h3>{t('editRole') || 'Edit Role'}</h3>
          </div>
        ) : (
          <>
            <h3>{t('roleDetails')}</h3>
            <div className="text-primary" role="presentation">
              <div className="edit-delete">
                <EditPencil onClick={toggleEditMode} style={{ cursor: 'pointer' }} />
              </div>
            </div>
          </>
        )}
      </div>

      <div
        style={{
          overflowY: 'scroll',
          height: 'calc(100vh - 150px)',
        }}>
        <div className="user-details-group">
          <div className="profile-card">
            {editMode ? (
              // Edit Mode Fields
              <div className="edit-fields-container">
                <div className="info-row-input">
                  <CustomInput
                    name="name"
                    inputLabel={t('roleName')}
                    type="text"
                    placeholder={t('enterName')}
                    value={roleDetails.name}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && !stringValidation(1, 100, roleDetails.name)}
                    errorMessage={t('roleNameRequired') || 'Role name is required'}
                  />
                </div>

                <div className="info-row-input">
                  <CustomInput
                    name="responsibility"
                    inputLabel={t('responsibility')}
                    type="text"
                    placeholder={t('describeHere')}
                    value={roleDetails.responsibility}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && !stringValidation(1, 100, roleDetails.responsibility)}
                    errorMessage={t('responsibilityRequired') || 'Responsibility is required'}
                  />
                </div>

                <div className="info-row-input">
                  <CheckBoxButtons
                    name="permissions"
                    list={permissionsList}
                    inputLabel={t('permissions')}
                    value={roleDetails.permissions}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && roleDetails.permissions.length === 0}
                    errorMessage={t('permissionsRequired') || 'At least one permission is required'}
                  />
                </div>

                <div
                  className="footer-actions"
                  style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                  <MyButton label={t('cancel')} buttonType="outline" onClickFunc={toggleEditMode} />
                  <MyButton
                    label={t('update')}
                    buttonType="submit"
                    onClickFunc={handleEditSubmit}
                    disabled={formLoading}
                    loading={formLoading}
                  />
                </div>
              </div>
            ) : (
              // View Mode Layout
              <div className="edit-fields-container">
                <div className="info-row-input" style={{ display: 'flex', gap: '15px' }}>
                  <CustomInput
                    name="name"
                    inputLabel={t('roleName')}
                    type="text"
                    placeholder={t('enterName')}
                    value={roleData?.name || ''}
                    onChangeValue={() => {}}
                    valueRequired
                    readOnly
                    fontSize="small"
                  />
                  <CustomInput
                    name="createdAt"
                    inputLabel={t('createdAt')}
                    type="text"
                    placeholder=""
                    value={roleData?.createdAt ? changeDateFormat(roleData.createdAt) : '-'}
                    onChangeValue={() => {}}
                    readOnly
                    fontSize="small"
                  />
                </div>

                <div className="info-row-input">
                  <CustomInput
                    name="responsibility"
                    inputLabel={t('responsibility')}
                    type="text"
                    placeholder={t('describeHere')}
                    value={roleData?.responsibility || ''}
                    onChangeValue={() => {}}
                    valueRequired
                    readOnly
                  />
                </div>

                <div className="info-row-input">
                  <CheckBoxButtons
                    name="permissions"
                    list={permissionsList}
                    inputLabel={t('permissions')}
                    value={roleData?.permissions || []}
                    onChangeValue={() => {}}
                    valueRequired
                    showOnlySelected
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleSidePanel;
